import React, { useEffect, useState } from "react";
import axios from "axios";
import PlanCard from "../../components/Plans/PlanCard";
import { useNavigate, useLocation } from "react-router-dom";
import "./Plans.css";
import { message, Modal } from "antd";
import usePlanStore from "../../store/usePlanStore";
import { useAvailablePlansStore } from "../../store/useAvialablePlansStore";
import PlansSkeleton from "../../components/Skeleton/PlansSkeleton";
import Footer from "../../components/Footer/Footer";

export default function Plans() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedPlanPrice = queryParams.get("plan"); // e.g. "99"
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [cancellingPlanId, setCancellingPlanId] = useState(null);

  const { availablePlans, loadingPlans, plansError, fetchAvailablePlans } =
    useAvailablePlansStore();
  const { planData, planLoading, fetchUserPlan } = usePlanStore();

  useEffect(() => {
    if (!planData && !planLoading) {
      fetchUserPlan();
    }
  }, [planData]);

  useEffect(() => {
    if (availablePlans.length === 0 && !loadingPlans && !plansError) {
      fetchAvailablePlans();
    }
  }, [availablePlans.length, loadingPlans, plansError, fetchAvailablePlans]);

  const handleSubscribe = async (plan) => {
    try {
      setLoadingPlanId(plan._id);
      const token = localStorage.getItem("userToken");
      const url = process.env.REACT_APP_API_BASE_URL;

      if (plan.isRecurring) {
        // ── Recurring plan → Stripe Checkout Session ──────────────────────────
        const response = await axios.post(
          `${url}/auth/v1/payment/checkout-session`,
          { id: plan._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.checkoutUrl) {
          // Redirect to Stripe-hosted checkout page
          window.location.href = response.data.checkoutUrl;
        } else {
          message.error(response.data.message || "Failed to create checkout session");
        }
      } else {
        // ── One-time plan → PaymentIntent (existing flow) ─────────────────────
        const response = await axios.post(
          `${url}/auth/v1/payment`,
          { id: plan._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.clientSecret && response.data.paymentIntentId) {
          navigate("/payment", {
            state: {
              clientSecret: response.data.clientSecret,
              plan,
              paymentIntentId: response.data.paymentIntentId,
            },
          });
        } else {
          message.error(
            response.data.message || "Failed to create payment intent"
          );
        }
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      message.error(
        // Surface the backend's actual reason. The previous copy guessed
        // "Subscription already exists" for every failure, including network
        // errors and auth failures, which sent people to check an account
        // that was fine.
        error.response?.data?.message ||
          "Failed to start checkout. Please try again."
      );
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleCancelSubscription = (planId) => {
    Modal.confirm({
      title: 'Cancel Subscription',
      content: 'Are you sure you want to cancel your subscription? Your plan will remain active until the end of the current billing period.',
      okText: 'Yes, Cancel',
      cancelText: 'No',
      okType: 'danger',
      okButtonProps: {
        style: { margin: "5px" }
      },
      cancelButtonProps: {
        style: { margin: "5px" }
      },
      onOk: async () => {
        try {
          setCancellingPlanId(planId);
          const token = localStorage.getItem("userToken");
          const url = process.env.REACT_APP_API_BASE_URL;
          const response = await axios.patch(
            `${url}/auth/v1/payment/${planId}/cancel`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.status) {
            message.success("Subscription cancelled successfully!");
            // Refresh the user plan data
            fetchUserPlan();
          } else {
            message.error(response.data.message || "Failed to cancel subscription");
          }
        } catch (error) {
          console.error("Error cancelling subscription:", error);
          message.error("Failed to cancel subscription. Please try again.");
        } finally {
          setCancellingPlanId(null);
        }
      },
    });
  };

  return (
    <>
      {loadingPlans ? (
        <PlansSkeleton />
      ) : (
        <div className="plans-container">
          <div className="plans-head">
            <h1>Choose your plan</h1>
            <p className="plans-sub">
              Pick the plan that fits your team. Change or cancel it whenever
              you need to.
            </p>
          </div>
          <div className="plans-grid">
            {availablePlans.map((plan) => (
              <PlanCard
                key={plan._id}
                plan={plan}
                onSubscribe={() => handleSubscribe(plan)}
                onCancel={() => handleCancelSubscription(planData?._id)}
                planData={planData}
                isSelected={String(plan.price) === selectedPlanPrice}
                loading={loadingPlanId === plan._id}
                cancelling={cancellingPlanId === planData?._id}
              />
            ))}
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}
